import { BillingStatement } from '@inflearn/billing-statement';
import {
  Badge,
  Button,
  COLOR_PALETTE,
  Divider,
  Ellipsis,
  Flex,
  Icon,
  Text,
  VisuallyHidden,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { Currency, useLang } from '@inflearn/i18n-tools';
import { faChevronRight } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { isEmpty } from 'class-validator';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { NEXT_PUBLIC_COURSE_API_URL } from '../../../../../appConfig';

import { INFLEARN_URL } from '../../../../constants/urls';
import dayjs from '../../../../utils/date';
import { NaverPayReceiptModal } from './NaverPayReceiptModal';
import type OrderDTO from './OrderDTO';
import { OrderStatus } from './OrderStatus';

type Props = { order: OrderDTO };

export const OrderCard = ({ order }: Props) => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const extraCourseCount = order.itemsCount > 0 ? order.itemsCount - 1 : 0;

  // FIXME: 임의 API라서 swagger에 등록되어 있지 않음. api url을 임의로 넣어둠. 등록되면 수정 필요
  const RECEIPT_API_URL = `${NEXT_PUBLIC_COURSE_API_URL}/client/api/v1/order/${order.id}/receipt`;

  const [naverPayReceiptModalOpened, naverPayReceiptModalHandler] = useDisclosure(false);
  const receiptButtonProps = useMemo(() => {
    const baseProps = { variant: 'default', size: 'xs' };

    if (order.paymentChannel === 'NAVERPAY') {
      return { ...baseProps, onClick: naverPayReceiptModalHandler.open };
    }

    return {
      ...baseProps,
      target: '_blank',
      href:
        isEmpty(order.receiptUrl) || isNil(order.receiptUrl) ? RECEIPT_API_URL : order.receiptUrl,
    };
  }, [order.paymentChannel, order.receiptUrl, naverPayReceiptModalHandler.open, RECEIPT_API_URL]);

  const isClip = order.orderMode === 'CLIP'; // MEMO: 클립 주문은 거래명세서를 조회할 수 없음. https://inflab-team.slack.com/archives/C094ZPASCU8/p1758247703624319
  const isCoursePackage = order.orderMode === 'COURSE_PACKAGE';

  const isLinkpay = order.paymentMethod.includes('LINK');

  const itemsCountLabel = isCoursePackage
    ? t('myOrders.coursePackage.includedCourseCount', { itemsCount: order.itemsCount })
    : extraCourseCount > 0
      ? t('외 {{extraCourseCount}} 건', { extraCourseCount })
      : null;

  return (
    <>
      <li>
        <Flex justify="space-between" align="center" mb={8}>
          <dl css={{ display: 'flex' }}>
            <dt aria-label={t('주문 날짜')}>
              <Text mr={4} component="span" fw={500} size="sm">
                {t('주문 날짜')}
              </Text>
            </dt>
            <dd>
              <Text component="span" fw={700} size="sm">
                {dayjs(order.createdAtUTC).locale(lang).tz(order.timezone).format('L')}
              </Text>
            </dd>
          </dl>

          {order.isPaypalOverseasWithoutReceipt ? null : (
            <Button
              component="a"
              href={
                isClip
                  ? INFLEARN_URL.CLIP_ORDER_DETAIL(order.id)
                  : INFLEARN_URL.ORDER_DETAIL(order.id)
              }
              target="_blank"
              rel="noreferrer"
              variant="subtle"
              color="dark"
              size="compact-xs"
              rightSection={<Icon icon={faChevronRight} />}
            >
              {t('자세히 보기')}
            </Button>
          )}
        </Flex>
        <Flex
          styles={{
            root: {
              border: `1px solid ${COLOR_PALETTE.gray[2]}`,
              borderRadius: '8px',
              padding: '16px',
              [desktopFirstMediaQuery.mobile]: {
                flexDirection: 'column',
                gap: 12,
              },
            },
          }}
        >
          <Flex
            gap={8}
            mr={48}
            direction="column"
            styles={{
              root: {
                width: 0,
                flex: 1,
                [desktopFirstMediaQuery.mobile]: { width: '100%' },
              },
            }}
          >
            <Flex direction={'column'} align={'flex-start'} gap={8}>
              <Badge
                variant="filled"
                radius="sm"
                size="md"
                color={
                  order.statusLabel === OrderStatus.PAYMENT_COMPLETE
                    ? 'infgreen'
                    : order.statusLabel === OrderStatus.PAYMENT_READY
                      ? 'orange'
                      : 'gray'
                }
              >
                {t(order.statusLabel)}
              </Badge>
              <dl css={{ display: 'flex' }}>
                <dt aria-label={t('주문 번호')}>
                  <Text component="span" fw={400} size="sm">
                    {t('주문 번호')}&nbsp;
                  </Text>
                </dt>
                <dd>
                  <Text component="span" fw={700} size="sm">
                    {order.id}
                  </Text>
                  {isLinkpay ? (
                    <Text component="span" fw={700} size="sm">
                      {' '}
                      (대신결제)
                    </Text>
                  ) : null}
                </dd>
              </dl>
            </Flex>
            <Flex>
              <Ellipsis>
                <Text component="span" size="sm" c="gray.7" fw={500}>
                  {order.title}
                </Text>
              </Ellipsis>
              {itemsCountLabel ? (
                <Text
                  component="span"
                  size="sm"
                  c="gray.9"
                  fw={700}
                  ml={8}
                  styles={{
                    root: { flexShrink: 0 },
                  }}
                >
                  {itemsCountLabel}
                </Text>
              ) : null}
            </Flex>
          </Flex>
          <Flex
            direction="column"
            gap={8}
            mr={36}
            styles={{
              root: {
                flexShrink: 0,
                width: 160,
                [desktopFirstMediaQuery.mobile]: {
                  width: '100%',
                },
              },
            }}
          >
            {order.discountedPrice ? (
              <>
                <Flex component="dl" justify="space-between">
                  <Text size="xs" c="gray.7" fw={400} component="dt">
                    {t('금액')}
                  </Text>
                  <Text size="xs" c="gray.7" fw={500} component="dd">
                    <Currency amount={order.displayRegularPrice} currency={order.currency} />
                  </Text>
                </Flex>

                <Flex component="dl" justify="space-between">
                  <VisuallyHidden>
                    <Text size="xs" c="gray.7" fw={400} component="dt">
                      {t('할인')}
                    </Text>
                  </VisuallyHidden>
                  <Text
                    size="xs"
                    c="gray.7"
                    fw={500}
                    component="dd"
                    styles={{
                      root: { marginLeft: 'auto' },
                    }}
                  >
                    <Currency amount={order.discountedPrice} currency={order.currency} />
                  </Text>
                </Flex>

                <Divider size="xs" />
              </>
            ) : null}
            <Flex component="dl" justify="space-between">
              <Text size="xs" c="gray.7" fw={700} component="dt">
                {t('합계')}
              </Text>
              <Text size="xs" c="gray.7" fw={700} component="dd">
                <Currency amount={order.payPrice} currency={order.currency} />
              </Text>
            </Flex>
          </Flex>
          <Flex
            gap={8}
            styles={{
              root: {
                flexDirection: 'column',
                flexShrink: 0,
                [desktopFirstMediaQuery.tablet]: {
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                },
              },
            }}
          >
            {order.isReceiptAvailable ? (
              <Button component="a" w={82} {...receiptButtonProps}>
                {t('영수증')}
              </Button>
            ) : null}
            {order.canGetInvoice && !isClip ? (
              <BillingStatement lang={lang} type="page" orderId={order.id} size="xs">
                {t('거래명세서')}
              </BillingStatement>
            ) : null}
          </Flex>
        </Flex>
      </li>
      <NaverPayReceiptModal
        opened={naverPayReceiptModalOpened}
        onClose={naverPayReceiptModalHandler.close}
        onNaverPayClick={() => {
          window.open('https://new-m.pay.naver.com/', '_blank', 'noreferrer');
        }}
      />
    </>
  );
};
